import React, { useCallback, useContext, useEffect, useState } from "react";
import * as Sentry from "@sentry/react";
import { observer } from "mobx-react";
import { useTranslation } from "react-i18next";
import { UI } from "@wwimmo/ui";
import styles from "./AssemblyUnlockModal.module.css";
import { RootStoreContext } from "src/stores/RootStore";

const AssemblyUnlockModalBase = () => {
    const { t } = useTranslation();
    const { agendaitemStore, assemblyStore, protocolStore } = useContext(RootStoreContext);
    const [isFirstClick, setIsFirstClick] = useState(true);
    const [hasProtocol, setHasProtocol] = useState(false);
    const [isCheckingProtocol, setIsCheckingProtocol] = useState(true);

    const assemblyId = assemblyStore.currentAssembly?.id;

    useEffect(() => {
        if (!agendaitemStore.isModalUnlockDisplayed || !assemblyId) return;

        let cancelled = false;
        setIsCheckingProtocol(true);

        protocolStore.hasAssemblyProtocolFile(assemblyId).then((exists) => {
            if (!cancelled) {
                setHasProtocol(exists);
                setIsCheckingProtocol(false);
            }
        });

        return () => {
            cancelled = true;
        };
    }, [agendaitemStore.isModalUnlockDisplayed, assemblyId, protocolStore]);

    const onCloseUnlockModal = useCallback(() => {
        agendaitemStore.setIsModalUnlockDisplayed(false);
        setIsFirstClick(true); // Reset state on close
    }, [agendaitemStore]);

    const onClickOkButton = useCallback(() => {
        if (hasProtocol && isFirstClick) {
            setIsFirstClick(false);
            return;
        }
        assemblyStore.updateAssemblyState(1);
        agendaitemStore.deleteAssemblyProtocolFiles();
        agendaitemStore.setIsModalUnlockDisplayed(false);
        setIsFirstClick(true);
    }, [hasProtocol, isFirstClick, agendaitemStore, assemblyStore]);

    const protocolExistsFirstContent = (
        <>
            <div className={styles.ModalTitle}>{t("screens.assemblies.agenda.modal.title")}</div>
            <div className={styles.ModalSubtitle}>{t("screens.assemblies.agenda.modal.subtitle1")}</div>
            <ul>
                <li>{t("screens.assemblies.agenda.modal.protocol_publish")}</li>
                <li>{t("screens.assemblies.agenda.modal.protocol_download")}</li>
            </ul>
            <div>{t("screens.assemblies.agenda.modal.protocol_republish")}</div>
        </>
    );

    const protocolExistsSecondContent = (
        <>
            <div className={styles.ModalTitle}>{t("screens.assemblies.agenda.modal.title")}</div>
            <div className={styles.ModalSubtitle}>{t("screens.assemblies.agenda.modal.subtitle2")}</div>
        </>
    );

    const noProtocolContent = (
        <>
            <div className={styles.ModalTitle}>{t("screens.assemblies.agenda.modal.title")}</div>
            <div className={styles.ModalSubtitle}>{t("screens.assemblies.agenda.modal.subtitle1")}</div>
            <ul>
                <li>{t("screens.assemblies.agenda.modal.assembly_conclusion")}</li>
            </ul>
        </>
    );

    const modalContent = isCheckingProtocol ? (
        <UI.RotatingSpinner noLogo size={30} />
    ) : !hasProtocol ? (
        noProtocolContent
    ) : isFirstClick ? (
        protocolExistsFirstContent
    ) : (
        protocolExistsSecondContent
    );

    return (
        <UI.Modal show={agendaitemStore.isModalUnlockDisplayed} isScrollable={true} centered>
            {modalContent}
            <div className={"d-flex justify-content-end mt-2"}>
                <UI.Button
                    label={t("labels.cancel")}
                    className={"mr-3"}
                    onClick={onCloseUnlockModal}
                    variant="outline-primary"
                />
                <UI.Button
                    label={t("labels.ok").toUpperCase()}
                    onClick={onClickOkButton}
                    disabled={isCheckingProtocol}
                />
            </div>
        </UI.Modal>
    );
};

export const AssemblyUnlockModal = Sentry.withProfiler(observer(AssemblyUnlockModalBase));
